import React, { useCallback, useEffect, useState } from 'react';
import styles from './FeatureVideoCard.module.css';

export type FeatureItem = {
  title: string;
  youtubeUrl: string;
  thumbnailUrl?: string;
};


export function getYouTubeVideoId(url: string): string | null {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  const embedMatch = trimmed.match(/(?:youtube\.com\/embed\/|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (embedMatch) return embedMatch[1];
  const watchMatch = trimmed.match(/(?:youtube\.com\/watch\?.*v=)([a-zA-Z0-9_-]{11})/);
  if (watchMatch) return watchMatch[1];
  return null;
}


export function getYouTubeEmbedUrl(videoId: string): string {
  return `https://www.youtube.com/embed/${videoId}`;
}


export function getYouTubeThumbnailUrl(videoId: string): string {
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}

type FeatureVideoCardProps = {
  item: FeatureItem;
};

const FeatureVideoCard: React.FC<FeatureVideoCardProps> = ({ item }) => {
  const { title, youtubeUrl, thumbnailUrl } = item;
  const [modalOpen, setModalOpen] = useState(false);

  const videoId = getYouTubeVideoId(youtubeUrl);
  const thumbnail = thumbnailUrl ?? (videoId ? getYouTubeThumbnailUrl(videoId) : '');
  const embedUrl = videoId ? getYouTubeEmbedUrl(videoId) : '';

  const openModal = useCallback(() => {
    if (embedUrl) setModalOpen(true);
  }, [embedUrl]);

  const closeModal = useCallback(() => setModalOpen(false), []);

  useEffect(() => {
    if (!modalOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [modalOpen, closeModal]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openModal();
      }
      if (e.key === 'Escape') closeModal();
    },
    [openModal, closeModal]
  );

  if (!videoId) return null;

  return (
    <>
      <div
        className={styles.card}
        onClick={openModal}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-label={`Play video: ${title}`}
      >
        <div className={styles.videoWrap}>
          <img
            src={thumbnail}
            alt=""
            className={styles.thumbnail}
            loading="lazy"
          />
          <div className={styles.overlay} />
          <div className={styles.playButton} aria-hidden />
        </div>
        <div className={styles.content}>
          <h3 className={styles.title}>{title}</h3>
        </div>
      </div>

      {modalOpen && (
        <div
          className={styles.modalBackdrop}
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-label={`Video: ${title}`}
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className={styles.modalClose}
              onClick={closeModal}
              aria-label="Close video"
            >
              ×
            </button>
            <div className={styles.iframeWrap}>
              <iframe
                src={embedUrl}
                title={title}
                className={styles.iframe}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FeatureVideoCard;
